import { useState } from "react";
import { useForm } from "react-hook-form";
import { Auth } from "aws-amplify";
import Button from "../../../common/components/controls/button/styled-button";
import { InfoText } from "../../../common/components/info_text";
import { ButtonSocial } from "../../../common/components/controls/button";
import { Field } from "../../../common/components/controls/field";
import { Select } from "../../../common/components/controls/select";
import { CheckBox } from "../../../common/components/controls/checkbox";
import { SectionDivider } from "../../../common/components/section_divider";
import { FormStepCard } from "../../../common/components/form_steps";
import { hasNumber, hasUpperCaseLetter } from "../../../common/helpers/utilities";
import { AuthErrors, ScreenProps } from "../dtos";
import { textChooser } from "../../../common/util/textChooser";
import countryCode from "../../../assets/json/countryCode.json";
import { PhoneRow, RegisterFormContainer, RegisterRow } from "./styled";
import { validatePhoneRegistered } from "../../../common/apis/private.api";

const initialState: {[name: string]: boolean} = {
  pass: false,
  user: false,
  phone: false,
  num: false,
  upper: false,
  accept: false
}

export const RegisterScreen = ({ dispatchScreen }: ScreenProps) => {

  const { register, handleSubmit, setValue } = useForm();
  const [errors, setErrors] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const countryCodeValues = countryCode.map((country) => ({
    value: country.dial_code, label: country.name
  }))

  const handleSignUpError = (errorType: AuthErrors) => {
    // email already registered
    if (errorType.code === "UsernameExistsException") {
      setErrors({ ...initialState, user: true });
      return;
    }
    // phone number already registered
    if (errorType.code === "PhoneExistsException"){
      setErrors({ ...initialState, phone: true });
      return;
    }
  }

  const handleValidationForm = (data: any) => {
    if (!hasNumber(data.pass)) {
      setErrors({ ...initialState, num: true });
      return true;
    }
    if (!hasUpperCaseLetter(data.pass)) {
      setErrors({ ...initialState, upper: true });
      return true;
    }
    if (!data.accept) {
      setErrors({ ...initialState, accept: true });
      return true;
    }

    return false;
  }

  const handlePhoneValidation = async (phone: string) => {
    try {
      const response = await validatePhoneRegistered(phone);
        
      // Phone number exists
      if (response.hasVerify) {
        return true;
      }
        
      // Phone number does not exist
      return false;
    } catch (error) {
      // API request failed or phone number not found (404 error)
      return false;
    }
  }

  const handleClassicRegister = async (data: any) => {

    const phoneNumber = `${data.phoneCode}${data.phone}`.trim();
    const fullname = `${data.name} ** ${data.last}`;

    setLoading(true);

    // making sure the right data is submitted
    if(!handleValidationForm(data)){

      // verified if phone exists
      const phoneExists = await handlePhoneValidation(phoneNumber);
    
      if(phoneExists) {
        handleSignUpError({code: "PhoneExistsException"});
        setLoading(false);
        return;
      }

      Auth.signUp({
        username: data.user,
        password: data.pass,
        attributes: {
          email: data.user,
          phone_number: phoneNumber,
          name: fullname,
        }
      }).then(() => {
        dispatchScreen({who: data.user, currentScreen: 2});
      }).catch((err) => {
        handleSignUpError(err);
        setLoading(false);
      })
    }
  }

  return (
    <FormStepCard title={textChooser("txt18")}>
      <RegisterFormContainer onSubmit={handleSubmit(handleClassicRegister)}>
        <InfoText text={textChooser("txt222")} type={"plain"} />{":"}

        <div className="card-social-btns">
          <ButtonSocial type="Google" hideText small />
          <ButtonSocial type="Facebook" hideText small />
        </div>

        <SectionDivider />
        <InfoText text={textChooser("txt21")} type={"plain"} />

        <RegisterRow>
          <Field
            type="text"
            hasIcon={true}
            hasError={false}
            label={textChooser("txt22")}
            placeholder={"John"}
            testId={"name-register-field"}
            maxLength={32}
            minLength={2}
            hooks={{ ...register("name", { required: true }) }}
          />
          <Field
            type="text"
            hasIcon={true}
            hasError={false}
            label={textChooser("txt23")}
            placeholder={"Smith"}
            testId={"last-register-field"}
            maxLength={35}
            minLength={2}
            hooks={{ ...register("last", { required: true }) }}
          />
        </RegisterRow>

        <Field
          type="email"
          hasIcon={true}
          hasError={errors.user}
          label={textChooser("txt30")}
          placeholder={textChooser("txt4")}
          testId={"user-register-field"}
          maxLength={35}
          minLength={6}
          hooks={{ ...register("user", { required: true }) }}
        />
        <InfoText
          text={textChooser("txt65")}
          type={"danger"}
          hidden={!errors.user}
        />

        <Field
          type="password"
          hasIcon={true}
          hasError={errors.pass}
          label={textChooser("txt31")}
          placeholder={textChooser("txt5")}
          testId={"pass-register-field"}
          maxLength={35}
          minLength={8}
          hooks={{ ...register("pass", { required: true }) }}
        />
        <InfoText
          text={textChooser("txt44")}
          type={"success"}
          hidden={!errors.num}
        />
        <InfoText
          text={textChooser("txt45")}
          type={"success"}
          hidden={!errors.upper}
        />

        <label>
          {textChooser("txt32")}
        </label>

        <PhoneRow>
          <Select
            values={countryCodeValues}
            currentValue={"+51"}
            hook={{ ...register("phoneCode", { required: true }) }}
          />
          <Field
            type="tel"
            hasIcon={true}
            hasError={errors.phone}
            placeholder={"999 999 999"}
            testId={"tel-register-field"}
            maxLength={15}
            minLength={9}
            hooks={{...register("phone", { required: true })}}
          />
        </PhoneRow>

        <div style={{ marginBottom: 32 }}>
          <CheckBox
            value={1}
            label={"al crear una cuenta con nosotros estas aceptando las politicas comerciales y los servicios de cookies impuestos por la entidad HRD."}
            name={"accept"}
            onChange={(e) => setValue("accept", e?.target.checked)}
            className={"primary"}
          />

          <InfoText
            text={textChooser("txt52")}
            hidden={!errors.accept}
            type="danger"
          />
          <InfoText
            text={textChooser("txt224")}
            hidden={!errors.phone}
            type="danger"
          />
        </div>

        <Button type="submit" disabled={loading}>
          {textChooser("txt6")}
        </Button>
      </RegisterFormContainer>
    </FormStepCard>
  )
}